import { useEffect, useState } from 'react';

export function useReferenceExpansion(
  referenceIdentifiers: string[],
  filteredReferenceIdentifiers: string[]
): {
  expandedReferences: Set<string>;
  expandAll: () => void;
  collapseAll: () => void;
  toggleReference: (referenceId: string) => void;
  allExpanded: boolean;
  anyExpanded: boolean;
} {
  const [expandedReferences, setExpandedReferences] = useState<Set<string>>(new Set());
  const [hasInitiallyExpanded, setHasInitiallyExpanded] = useState(false);

  // Sync expandedReferences when referenceIdentifiers changes (remove stale refs)
  useEffect(() => {
    setExpandedReferences((previous) => {
      const validReferences = referenceIdentifiers.filter((reference) => previous.has(reference));
      if (validReferences.length === previous.size) {
        return previous;
      }
      return new Set(validReferences);
    });
  }, [referenceIdentifiers]);

  // Auto-expand all references on first load
  useEffect(() => {
    if (referenceIdentifiers.length > 0 && !hasInitiallyExpanded) {
      setExpandedReferences(new Set(referenceIdentifiers));
      setHasInitiallyExpanded(true);
    }
  }, [referenceIdentifiers, hasInitiallyExpanded]);

  const expandAll = () => setExpandedReferences(new Set(filteredReferenceIdentifiers));
  const collapseAll = () => setExpandedReferences(new Set());

  const toggleReference = (referenceId: string) => {
    setExpandedReferences((previous) => {
      const next = new Set(previous);
      if (next.has(referenceId)) {
        next.delete(referenceId);
      } else {
        next.add(referenceId);
      }
      return next;
    });
  };

  const allExpanded =
    expandedReferences.size === filteredReferenceIdentifiers.length &&
    filteredReferenceIdentifiers.length > 0;
  const anyExpanded = expandedReferences.size > 0;

  return {
    expandedReferences,
    expandAll,
    collapseAll,
    toggleReference,
    allExpanded,
    anyExpanded,
  };
}
